import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Eye, Upload, ImagePlus, Check, X, Maximize2, Copy, Paintbrush, ChevronDown, ChevronRight, Plus, Trash2,
  Download, Send, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_SIMILAR = { image_url: '', link_url: '', name: '', price: '' };
const EMPTY_SEGMENT = { text: '', bold: false, color: '#1a1a1a' };
const EMPTY_SIG_LINE = { segments: [{ ...EMPTY_SEGMENT }], link: '' };

const SIGNATURE_FONTS = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, Times New Roman, serif', label: 'Georgia' },
  { value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
  { value: 'Courier New, Courier, monospace', label: 'Courier New' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
];

const makeSigLines = () => [
  { segments: [{ ...EMPTY_SEGMENT }], link: '' },
  { segments: [{ ...EMPTY_SEGMENT }], link: '' },
  { segments: [{ ...EMPTY_SEGMENT }], link: '' },
  { segments: [{ ...EMPTY_SEGMENT }], link: '' },
];

const DEFAULT_FORM = {
  logo_url: '',
  logo_height: 80,
  intro_text: '',
  vehicle_images: ['', '', ''],
  create_deal_label: 'Create Your Deal',
  create_deal_link: '',
  test_drive_link: '',
  window_sticker_link: '',
  warranty_link: '',
  preowned_link: '',
  similar_vehicles_label: 'Similar Vehicles',
  similar_vehicles: [
    { ...EMPTY_SIMILAR },
    { ...EMPTY_SIMILAR },
    { ...EMPTY_SIMILAR },
  ],
  ending_text: '',
  signature_lines: makeSigLines(),
  signature_font: 'Arial, Helvetica, sans-serif',
  signature_logo_url: '',
  signature_logo_link: '',
  signature_logo_height: 80,
};

function SectionToggle({ label, open, onToggle, children }) {
  return (
    <div className="border border-[#262626] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-[#0F0F0F] hover:bg-[#151515] transition-colors text-left"
      >
        <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">{label}</span>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="p-3 space-y-3 bg-[#0A0A0A]">{children}</div>}
    </div>
  );
}

export default function ManualTemplateBuilder() {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [showPopout, setShowPopout] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipient: '', subject: 'Sample Template Preview' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showLogoLibrary, setShowLogoLibrary] = useState(false);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openSections, setOpenSections] = useState({
    logo: true, intro: true, vehicle: true, buttons: true, similar: true, ending: true, signature: true
  });

  const toggleSection = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  const updateVehicleImage = (idx, val) => {
    setForm(p => {
      const imgs = [...p.vehicle_images];
      imgs[idx] = val;
      return { ...p, vehicle_images: imgs };
    });
  };

  const updateSimilarVehicle = (idx, field, val) => {
    setForm(p => {
      const svs = [...p.similar_vehicles];
      svs[idx] = { ...svs[idx], [field]: val };
      return { ...p, similar_vehicles: svs };
    });
  };

  const updateSignatureLine = (idx, field, val) => {
    setForm(p => {
      const lines = [...p.signature_lines];
      lines[idx] = { ...lines[idx], [field]: val };
      return { ...p, signature_lines: lines };
    });
  };

  const updateSegment = (lineIdx, segIdx, field, val) => {
    setForm(p => {
      const lines = [...p.signature_lines];
      const segs = [...lines[lineIdx].segments];
      segs[segIdx] = { ...segs[segIdx], [field]: val };
      lines[lineIdx] = { ...lines[lineIdx], segments: segs };
      return { ...p, signature_lines: lines };
    });
  };

  const addSegment = (lineIdx) => {
    setForm(p => {
      const lines = [...p.signature_lines];
      lines[lineIdx] = { ...lines[lineIdx], segments: [...lines[lineIdx].segments, { ...EMPTY_SEGMENT }] };
      return { ...p, signature_lines: lines };
    });
  };

  const removeSegment = (lineIdx, segIdx) => {
    setForm(p => {
      const lines = [...p.signature_lines];
      const segs = [...lines[lineIdx].segments];
      segs.splice(segIdx, 1);
      lines[lineIdx] = { ...lines[lineIdx], segments: segs.length > 0 ? segs : [{ ...EMPTY_SEGMENT }] };
      return { ...p, signature_lines: lines };
    });
  };

  const addSignatureLine = () => {
    setForm(p => ({ ...p, signature_lines: [...p.signature_lines, { segments: [{ ...EMPTY_SEGMENT }], link: '' }] }));
  };

  const removeSignatureLine = (idx) => {
    setForm(p => {
      const lines = [...p.signature_lines];
      lines.splice(idx, 1);
      return { ...p, signature_lines: lines };
    });
  };

  const fetchLogoLibrary = async () => {
    try {
      const res = await axios.get(`${API}/api/upload/logos`, { headers });
      setLogoLibrary(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleLogoUpload = async (file) => {
    if (!file || file.size > 2 * 1024 * 1024) { toast.error('Max 2MB'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/api/upload/logo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(p => ({ ...p, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
      fetchLogoLibrary();
    } catch (e) { toast.error('Upload failed'); }
    setUploading(false);
  };

  // Build email HTML from form data
  const buildEmailHTML = useCallback(() => {
    const { logo_url, intro_text, vehicle_images, create_deal_label, create_deal_link, test_drive_link, window_sticker_link, warranty_link, preowned_link, similar_vehicles_label, similar_vehicles, ending_text, signature_lines, signature_font, signature_logo_url } = form;

    let html = '<div style="font-family:Arial,Helvetica,sans-serif;max-width:680px;margin:0 auto;padding:40px 0 0 0;color:#1a1a1a">';

    // Logo
    if (logo_url) {
      const lh = form.logo_height || 80;
      html += `<div style="text-align:center;margin:0 0 24px 0"><img src="${logo_url}" alt="Dealership Logo" style="height:${lh}px;display:inline-block" /></div>`;
    }

    // Introduction
    if (intro_text) {
      html += `<div style="margin:0 0 20px 0;font-size:15px;line-height:1.6">${intro_text.replace(/\n/g, '<br/>')}</div>`;
    }

    // Vehicle images
    const activeImages = vehicle_images.filter(u => u.trim());
    if (activeImages.length > 0) {
      html += '<div style="text-align:center;margin:0 0 20px 0">';
      if (activeImages.length === 1) {
        html += `<img src="${activeImages[0]}" alt="Vehicle" style="max-width:100%;height:auto;border-radius:8px;border:1px solid #e0e0e0" />`;
      } else {
        html += '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;width:100%;table-layout:fixed"><tr>';
        activeImages.forEach((img, i) => {
          html += `<td style="padding:0 3px;vertical-align:top;text-align:center"><img src="${img}" alt="Vehicle ${i + 1}" style="width:100%;height:auto;border-radius:6px;border:1px solid #e0e0e0" /></td>`;
        });
        html += '</tr></table>';
      }
      html += '</div>';
    }

    // CTA Buttons (Create Deal + Test Drive)
    const ctaButtons = [];
    if (create_deal_link) ctaButtons.push({ label: create_deal_label || 'Create Your Deal', url: create_deal_link, bg: '#1a1a1a' });
    if (test_drive_link) ctaButtons.push({ label: 'Schedule Test Drive', url: test_drive_link, bg: '#2563eb' });
    if (ctaButtons.length > 0) {
      html += '<div style="text-align:center;margin:0 0 16px 0"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>';
      ctaButtons.forEach((btn, i) => {
        html += `${i > 0 ? '<td style="width:12px"></td>' : ''}<td style="background-color:${btn.bg};border-radius:8px;padding:14px 32px;text-align:center"><a href="${btn.url}" target="_blank" rel="noopener noreferrer" style="color:#ffffff;font-size:15px;font-weight:bold;font-family:Arial,sans-serif;text-decoration:none">${btn.label}</a></td>`;
      });
      html += '</tr></table></div>';
    }

    // Secondary buttons row (Window Sticker, Warranty, Pre-Owned)
    const secondaryBtns = [];
    if (window_sticker_link) secondaryBtns.push({ label: 'Window Sticker', url: window_sticker_link, bg: '#2563eb' });
    if (warranty_link) secondaryBtns.push({ label: 'New Vehicle Warranty', url: warranty_link, bg: '#2563eb' });
    if (preowned_link) secondaryBtns.push({ label: 'Pre-Owned Vehicles', url: preowned_link, bg: '#2563eb' });
    if (secondaryBtns.length > 0) {
      html += '<div style="text-align:center;margin:0 0 20px 0"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>';
      secondaryBtns.forEach((btn, i) => {
        html += `${i > 0 ? '<td style="width:12px"></td>' : ''}<td style="background-color:${btn.bg};border-radius:6px;padding:12px 24px;text-align:center"><a href="${btn.url}" target="_blank" rel="noopener noreferrer" style="color:#ffffff;font-size:14px;font-weight:bold;font-family:Arial,sans-serif;text-decoration:none">${btn.label}</a></td>`;
      });
      html += '</tr></table></div>';
    }

    // Similar Vehicles — all images same size via equal-width table cells
    const activeSimilar = similar_vehicles.filter(sv => sv.image_url.trim() || sv.name.trim());
    if (activeSimilar.length > 0) {
      const heading = similar_vehicles_label || 'Similar Vehicles';
      html += `<div style="margin:24px 0 20px 0"><div style="text-align:center;margin:0 0 12px 0;font-size:16px;font-weight:bold;color:#1a1a1a">${heading}</div>`;
      const colWidth = Math.floor(100 / activeSimilar.length);
      html += '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto;width:100%;table-layout:fixed"><tr>';
      activeSimilar.forEach((sv, i) => {
        const linkOpen = sv.link_url ? `<a href="${sv.link_url}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;color:inherit">` : '';
        const linkClose = sv.link_url ? '</a>' : '';
        html += `<td style="padding:0 4px;vertical-align:top;text-align:center">${linkOpen}`;
        if (sv.image_url) {
          html += `<img src="${sv.image_url}" alt="${sv.name || 'Vehicle'}" style="width:100%;height:auto;border-radius:6px;border:1px solid #e0e0e0;margin-bottom:6px" />`;
        }
        if (sv.name) html += `<div style="font-size:13px;font-weight:bold;color:#1a1a1a;margin-bottom:2px">${sv.name}</div>`;
        if (sv.price) html += `<div style="font-size:13px;color:#16a34a;font-weight:bold">${sv.price}</div>`;
        html += `${linkClose}</td>`;
      });
      html += '</tr></table></div>';
    }

    // Ending text
    if (ending_text) {
      html += `<div style="margin:20px 0;font-size:15px;line-height:1.6">${ending_text.replace(/\n/g, '<br/>')}</div>`;
    }

    // Signature — no separator, same font size as body, custom font, per-segment bold/color
    const activeLines = signature_lines.filter(l => l.segments.some(s => s.text.trim()));
    if (activeLines.length > 0) {
      const sigFont = signature_font || 'Arial, Helvetica, sans-serif';
      html += `<div style="margin:20px 0 0 0;font-size:15px;line-height:1.6;font-family:${sigFont};color:#1a1a1a">`;
      activeLines.forEach(line => {
        const hasLink = line.link && line.link.trim();
        if (hasLink) html += `<a href="${line.link}" target="_blank" rel="noopener noreferrer" style="text-decoration:underline;font-family:${sigFont};font-size:15px;color:#2563eb">`;
        line.segments.forEach(seg => {
          if (!seg.text) return;
          const weight = seg.bold ? 'font-weight:bold;' : '';
          const clr = seg.color && seg.color !== '#1a1a1a' ? `color:${seg.color};` : '';
          if (weight || clr) {
            html += `<span style="${weight}${clr}">${seg.text}</span>`;
          } else {
            html += seg.text;
          }
        });
        if (hasLink) html += '</a>';
        html += '<br/>';
      });
      html += '</div>';
    }

    // Signature logo
    if (signature_logo_url) {
      const h = form.signature_logo_height || 80;
      const logoImg = `<img src="${signature_logo_url}" alt="" style="height:${h}px;display:inline-block" />`;
      if (form.signature_logo_link && form.signature_logo_link.trim()) {
        html += `<div style="margin:12px 0 0 0"><a href="${form.signature_logo_link}" target="_blank" rel="noopener noreferrer">${logoImg}</a></div>`;
      } else {
        html += `<div style="margin:12px 0 0 0">${logoImg}</div>`;
      }
    }

    html += '</div>';
    return html;
  }, [form]);

  const copyHTML = () => {
    navigator.clipboard.writeText(buildEmailHTML());
    toast.success('HTML copied to clipboard');
  };

  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const container = document.createElement('div');
      container.innerHTML = buildEmailHTML();
      container.style.padding = '20px';
      container.style.background = '#ffffff';
      document.body.appendChild(container);
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: 'manual-template.pdf',
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }).from(container).save();
      document.body.removeChild(container);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF error:', e);
      toast.error('Failed to generate PDF');
    }
    setDownloadingPdf(false);
  };

  const sendEmail = async () => {
    if (!emailForm.recipient.trim()) { toast.error('Enter a recipient email'); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/api/samples/send-manual-email`, {
        recipient_email: emailForm.recipient.trim(),
        subject: emailForm.subject.trim() || 'Sample Template Preview',
        html_body: buildEmailHTML(),
      }, { headers });
      toast.success(`Email sent to ${emailForm.recipient}`);
      setShowEmailDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    }
    setSendingEmail(false);
  };

  const resetForm = () => {
    if (window.confirm('Reset all fields?')) {
      setForm({
        ...DEFAULT_FORM,
        similar_vehicles: [{ ...EMPTY_SIMILAR }, { ...EMPTY_SIMILAR }, { ...EMPTY_SIMILAR }],
        vehicle_images: ['', '', ''],
        signature_lines: makeSigLines(),
      });
    }
  };

  const previewHTML = buildEmailHTML();

  return (
    <div data-testid="manual-template-builder">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ── Left: Form ── */}
        <div className="space-y-3">
          {/* Logo */}
          <SectionToggle label="Logo" open={openSections.logo} onToggle={() => toggleSection('logo')}>
            {form.logo_url ? (
              <div className="p-2 bg-[#111] border border-[#262626] rounded-lg space-y-2">
                <div className="flex items-start justify-between">
                  <img src={form.logo_url} alt="" style={{ height: `${form.logo_height || 80}px` }} className="object-contain rounded" onError={e => { e.target.style.display = 'none'; }} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, logo_url: '', logo_height: 80 }))} className="text-slate-500 hover:text-red-400 h-7 px-2 shrink-0" data-testid="manual-logo-clear">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, logo_height: Math.max(20, (p.logo_height || 80) - 10) }))}
                    className="w-7 h-7 flex items-center justify-center rounded border border-[#333] bg-[#0A0A0A] text-slate-400 hover:text-white hover:border-[#555] transition-colors text-sm font-bold"
                    data-testid="manual-logo-smaller"
                  >
                    −
                  </button>
                  <span className="text-[10px] text-slate-500 font-mono w-8 text-center">{form.logo_height || 80}</span>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, logo_height: Math.min(300, (p.logo_height || 80) + 10) }))}
                    className="w-7 h-7 flex items-center justify-center rounded border border-[#333] bg-[#0A0A0A] text-slate-400 hover:text-white hover:border-[#555] transition-colors text-sm font-bold"
                    data-testid="manual-logo-larger"
                  >
                    +
                  </button>
                  <span className="text-[10px] text-slate-600 ml-1">px</span>
                </div>
              </div>
            ) : null}
            <div className="flex gap-2">
              <Input
                value={form.logo_url}
                onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))}
                placeholder="Paste logo image URL"
                className="bg-[#111] border-[#333] h-9 text-sm flex-1"
                data-testid="manual-logo-url"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => { setShowLogoLibrary(!showLogoLibrary); if (!showLogoLibrary) fetchLogoLibrary(); }}
                className="border-[#333] text-[10px] font-bold tracking-wider uppercase text-slate-400 h-9 px-3 shrink-0"
                data-testid="manual-logo-library-btn"
              >
                <ImagePlus className="w-3 h-3 mr-1" /> Library
              </Button>
            </div>
            {showLogoLibrary && (
              <div className="grid grid-cols-5 gap-2 mt-2" data-testid="manual-logo-library">
                {logoLibrary.map(logo => (
                  <div
                    key={logo.logo_id}
                    className={`relative cursor-pointer rounded-lg border-2 p-1.5 flex items-center justify-center h-16 transition-all ${form.logo_url === logo.url ? 'border-red-500 bg-red-500/10' : 'border-[#333] bg-[#111] hover:border-[#555]'}`}
                    onClick={() => { setForm(p => ({ ...p, logo_url: logo.url })); setShowLogoLibrary(false); }}
                  >
                    <img src={logo.url} alt="" className="max-h-12 max-w-full object-contain" />
                    {form.logo_url === logo.url && (
                      <div className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                <label className={`cursor-pointer rounded-lg border-2 border-dashed border-[#333] hover:border-red-600/40 p-1.5 flex flex-col items-center justify-center h-16 transition-all ${uploading ? 'opacity-50' : ''}`}>
                  {uploading ? <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /> : <><Upload className="w-4 h-4 text-slate-500 mb-0.5" /><span className="text-[8px] font-bold text-slate-500 uppercase">Upload</span></>}
                  <input type="file" accept="image/*" className="hidden" onChange={e => { handleLogoUpload(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
              </div>
            )}
          </SectionToggle>

          {/* Introduction */}
          <SectionToggle label="Introduction" open={openSections.intro} onToggle={() => toggleSection('intro')}>
            <textarea
              value={form.intro_text}
              onChange={e => setForm(p => ({ ...p, intro_text: e.target.value }))}
              placeholder="Thank you for your interest in the 2025 Cadillac Escalade! We're excited to help you find the perfect vehicle..."
              className="w-full bg-[#111] border border-[#333] rounded-lg p-3 text-sm text-white placeholder:text-slate-600 min-h-[80px] resize-y focus:outline-none focus:border-red-600/50"
              data-testid="manual-intro-text"
            />
          </SectionToggle>

          {/* Vehicle Images */}
          <SectionToggle label="Vehicle Images (up to 3)" open={openSections.vehicle} onToggle={() => toggleSection('vehicle')}>
            {[0, 1, 2].map(i => (
              <div key={i}>
                <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Image {i + 1}</Label>
                <Input
                  value={form.vehicle_images[i]}
                  onChange={e => updateVehicleImage(i, e.target.value)}
                  placeholder={`https://pictures.dealer.com/vehicle-photo-${i + 1}.jpg`}
                  className="mt-1 bg-[#111] border-[#333] h-9 text-sm"
                  data-testid={`manual-vehicle-image-${i}`}
                />
                {form.vehicle_images[i] && (
                  <div className="mt-1.5">
                    <img src={form.vehicle_images[i]} alt="" className="h-12 rounded border border-[#333] object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
            ))}
          </SectionToggle>

          {/* CTA Buttons */}
          <SectionToggle label="Button Links" open={openSections.buttons} onToggle={() => toggleSection('buttons')}>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Primary Button Label</Label>
              <Input value={form.create_deal_label} onChange={e => setForm(p => ({ ...p, create_deal_label: e.target.value }))} placeholder="Create Your Deal" className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-deal-label" />
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">{form.create_deal_label || 'Primary Button'} Link</Label>
              <Input value={form.create_deal_link} onChange={e => setForm(p => ({ ...p, create_deal_link: e.target.value }))} placeholder="https://shop.dealer.com/express/..." className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-deal-link" />
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Schedule Test Drive</Label>
              <Input value={form.test_drive_link} onChange={e => setForm(p => ({ ...p, test_drive_link: e.target.value }))} placeholder="https://www.dealer.com/testdrive.aspx" className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-testdrive-link" />
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Window Sticker</Label>
              <Input value={form.window_sticker_link} onChange={e => setForm(p => ({ ...p, window_sticker_link: e.target.value }))} placeholder="https://www.windowsticker.forddirect.com/..." className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-sticker-link" />
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">New Vehicle Warranty</Label>
              <Input value={form.warranty_link} onChange={e => setForm(p => ({ ...p, warranty_link: e.target.value }))} placeholder="https://www.dealer.com/warranty" className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-warranty-link" />
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Pre-Owned Vehicles</Label>
              <Input value={form.preowned_link} onChange={e => setForm(p => ({ ...p, preowned_link: e.target.value }))} placeholder="https://www.dealer.com/used-vehicles/" className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-preowned-link" />
            </div>
          </SectionToggle>

          {/* Similar Vehicles */}
          <SectionToggle label="Similar Vehicles (up to 3)" open={openSections.similar} onToggle={() => toggleSection('similar')}>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Section Heading</Label>
              <Input
                value={form.similar_vehicles_label}
                onChange={e => setForm(p => ({ ...p, similar_vehicles_label: e.target.value }))}
                placeholder="Similar Vehicles"
                className="mt-1 bg-[#111] border-[#333] h-9 text-sm"
                data-testid="manual-similar-label"
              />
            </div>
            {[0, 1, 2].map(i => (
              <div key={i} className="p-2.5 bg-[#111] border border-[#1A1A1A] rounded-lg space-y-2">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Vehicle {i + 1}</span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-slate-600">Name</Label>
                    <Input value={form.similar_vehicles[i].name} onChange={e => updateSimilarVehicle(i, 'name', e.target.value)} placeholder="2025 Escalade Sport" className="mt-0.5 bg-[#0A0A0A] border-[#262626] h-8 text-xs" data-testid={`manual-similar-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-600">Price</Label>
                    <Input value={form.similar_vehicles[i].price} onChange={e => updateSimilarVehicle(i, 'price', e.target.value)} placeholder="$89,990" className="mt-0.5 bg-[#0A0A0A] border-[#262626] h-8 text-xs" data-testid={`manual-similar-price-${i}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600">Image URL</Label>
                  <Input value={form.similar_vehicles[i].image_url} onChange={e => updateSimilarVehicle(i, 'image_url', e.target.value)} placeholder="https://pictures.dealer.com/similar-vehicle.jpg" className="mt-0.5 bg-[#0A0A0A] border-[#262626] h-8 text-xs" data-testid={`manual-similar-image-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-600">Link URL</Label>
                  <Input value={form.similar_vehicles[i].link_url} onChange={e => updateSimilarVehicle(i, 'link_url', e.target.value)} placeholder="https://www.dealer.com/inventory/vehicle-details..." className="mt-0.5 bg-[#0A0A0A] border-[#262626] h-8 text-xs" data-testid={`manual-similar-link-${i}`} />
                </div>
                {form.similar_vehicles[i].image_url && (
                  <img src={form.similar_vehicles[i].image_url} alt="" className="h-14 rounded border border-[#262626] object-cover" onError={e => { e.target.style.display = 'none'; }} />
                )}
              </div>
            ))}
          </SectionToggle>

          {/* Ending */}
          <SectionToggle label="Ending" open={openSections.ending} onToggle={() => toggleSection('ending')}>
            <textarea
              value={form.ending_text}
              onChange={e => setForm(p => ({ ...p, ending_text: e.target.value }))}
              placeholder="I look forward to speaking with you soon! Please don't hesitate to reach out if you have any questions."
              className="w-full bg-[#111] border border-[#333] rounded-lg p-3 text-sm text-white placeholder:text-slate-600 min-h-[60px] resize-y focus:outline-none focus:border-red-600/50"
              data-testid="manual-ending-text"
            />
          </SectionToggle>

          {/* Signature */}
          <SectionToggle label="Signature" open={openSections.signature} onToggle={() => toggleSection('signature')}>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Signature Font</Label>
              <Select value={form.signature_font} onValueChange={v => setForm(p => ({ ...p, signature_font: v }))}>
                <SelectTrigger className="mt-1 bg-[#111] border-[#333] h-9 text-sm" data-testid="manual-sig-font">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  {SIGNATURE_FONTS.map(f => (
                    <SelectItem key={f.value} value={f.value} className="text-white hover:bg-[#262626]">
                      <span style={{ fontFamily: f.value }}>{f.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Lines</Label>
              {form.signature_lines.map((line, i) => (
                <div key={i} className="p-2.5 bg-[#111] border border-[#1A1A1A] rounded-lg space-y-1.5">
                  {/* Segments */}
                  {line.segments.map((seg, s) => (
                    <div key={s} className="flex gap-1.5 items-center">
                      <Input
                        value={seg.text}
                        onChange={e => updateSegment(i, s, 'text', e.target.value)}
                        placeholder={s === 0 ? (i === 0 ? 'Jacob Baldridge' : i === 1 ? 'Sales Manager' : i === 2 ? 'Earnhardt Cadillac' : '(555) 123-4567') : 'continued text...'}
                        className="bg-[#0A0A0A] border-[#262626] h-8 text-xs flex-1"
                        data-testid={`manual-sig-text-${i}-${s}`}
                      />
                      <button
                        type="button"
                        onClick={() => updateSegment(i, s, 'bold', !seg.bold)}
                        className={`w-7 h-8 flex items-center justify-center rounded border text-xs font-black transition-all shrink-0 ${seg.bold ? 'bg-red-600/20 border-red-600/40 text-red-400' : 'bg-[#0A0A0A] border-[#262626] text-slate-500 hover:text-white'}`}
                        title="Bold"
                        data-testid={`manual-sig-bold-${i}-${s}`}
                      >
                        B
                      </button>
                      <div className="relative shrink-0">
                        <input
                          type="color"
                          value={seg.color || '#1a1a1a'}
                          onChange={e => updateSegment(i, s, 'color', e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer w-7 h-8"
                          data-testid={`manual-sig-color-${i}-${s}`}
                        />
                        <div
                          className="w-7 h-8 rounded border border-[#262626] flex items-center justify-center cursor-pointer hover:border-[#555] transition-colors"
                          style={{ backgroundColor: seg.color && seg.color !== '#1a1a1a' ? seg.color : 'transparent' }}
                        >
                          <span className="text-[9px] font-bold" style={{ color: seg.color && seg.color !== '#1a1a1a' ? '#fff' : '#666' }}>A</span>
                        </div>
                      </div>
                      {line.segments.length > 1 && (
                        <button type="button" onClick={() => removeSegment(i, s)} className="text-slate-600 hover:text-red-400 shrink-0" data-testid={`manual-sig-seg-remove-${i}-${s}`}>
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  <div className="flex gap-1.5 items-center">
                    <button
                      type="button"
                      onClick={() => addSegment(i)}
                      className="text-[9px] font-bold tracking-wider text-slate-600 hover:text-red-400 uppercase flex items-center gap-0.5 transition-colors"
                      data-testid={`manual-sig-add-seg-${i}`}
                    >
                      <Plus className="w-2.5 h-2.5" /> Segment
                    </button>
                    <div className="flex-1" />
                    <Input
                      value={line.link}
                      onChange={e => updateSignatureLine(i, 'link', e.target.value)}
                      placeholder="https://... (optional line hyperlink)"
                      className="bg-[#0A0A0A] border-[#262626] h-7 text-[11px] text-slate-500 placeholder:text-slate-700 flex-1"
                      data-testid={`manual-sig-link-${i}`}
                    />
                    {form.signature_lines.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSignatureLine(i)} className="text-slate-600 hover:text-red-400 h-7 px-1.5" data-testid={`manual-sig-remove-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addSignatureLine} className="border-[#333] text-[10px] font-bold tracking-wider uppercase text-slate-500 h-7 w-full" data-testid="manual-sig-add-line">
                <Plus className="w-3 h-3 mr-1" /> Add Line
              </Button>
            </div>
            <div>
              <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Signature Logo / Badge</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.signature_logo_url}
                  onChange={e => setForm(p => ({ ...p, signature_logo_url: e.target.value }))}
                  placeholder="Paste image URL or upload"
                  className="bg-[#111] border-[#333] h-9 text-sm flex-1"
                  data-testid="manual-sig-logo-url"
                />
                <label className={`flex items-center justify-center gap-1 px-3 h-9 border border-[#333] rounded-md text-slate-400 hover:text-red-400 hover:border-red-600/30 cursor-pointer transition-all text-[10px] font-bold tracking-wider uppercase shrink-0 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploading ? <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /> : <Upload className="w-3 h-3" />}
                  <span className="hidden sm:inline">Upload</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    data-testid="manual-sig-logo-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file || file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
                      setUploading(true);
                      const fd = new FormData(); fd.append('file', file);
                      try {
                        const res = await axios.post(`${API}/api/upload/image`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                        setForm(p => ({ ...p, signature_logo_url: res.data.image_url }));
                        toast.success('Image uploaded');
                      } catch (err) { toast.error('Upload failed'); }
                      setUploading(false);
                    }}
                  />
                </label>
                {form.signature_logo_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, signature_logo_url: '', signature_logo_link: '' }))} className="text-slate-500 hover:text-red-400 h-9 px-2 shrink-0">
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
              {form.signature_logo_url && (
                <>
                  <div className="mt-1.5 flex items-end gap-3">
                    <img src={form.signature_logo_url} alt="" style={{ height: `${form.signature_logo_height || 80}px` }} className="rounded border border-[#333] object-contain" onError={e => { e.target.style.display = 'none'; }} />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, signature_logo_height: Math.max(20, (p.signature_logo_height || 80) - 10) }))}
                        className="w-7 h-7 flex items-center justify-center rounded border border-[#333] bg-[#111] text-slate-400 hover:text-white hover:border-[#555] transition-colors text-sm font-bold"
                        data-testid="manual-sig-logo-smaller"
                      >
                        −
                      </button>
                      <span className="text-[10px] text-slate-500 font-mono w-8 text-center">{form.signature_logo_height || 80}</span>
                      <button
                        type="button"
                        onClick={() => setForm(p => ({ ...p, signature_logo_height: Math.min(300, (p.signature_logo_height || 80) + 10) }))}
                        className="w-7 h-7 flex items-center justify-center rounded border border-[#333] bg-[#111] text-slate-400 hover:text-white hover:border-[#555] transition-colors text-sm font-bold"
                        data-testid="manual-sig-logo-larger"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="mt-1.5">
                    <Input
                      value={form.signature_logo_link}
                      onChange={e => setForm(p => ({ ...p, signature_logo_link: e.target.value }))}
                      placeholder="https://... (optional link when clicked)"
                      className="bg-[#0A0A0A] border-[#262626] h-8 text-[11px] text-slate-500 placeholder:text-slate-700"
                      data-testid="manual-sig-logo-link"
                    />
                  </div>
                </>
              )}
            </div>
          </SectionToggle>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2 flex-wrap">
            <Button onClick={downloadPDF} disabled={downloadingPdf} variant="outline" className="flex-1 border-[#333] text-slate-400 text-xs font-bold tracking-wider uppercase h-10 min-w-[120px]" data-testid="manual-download-pdf">
              {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} PDF
            </Button>
            <Button onClick={() => setShowEmailDialog(true)} variant="outline" className="flex-1 border-[#333] text-slate-400 text-xs font-bold tracking-wider uppercase h-10 min-w-[120px]" data-testid="manual-send-email-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Email
            </Button>
            <Button onClick={copyHTML} variant="outline" className="flex-1 border-[#333] text-slate-400 text-xs font-bold tracking-wider uppercase h-10 min-w-[120px]" data-testid="manual-copy-html">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> HTML
            </Button>
            <Button onClick={resetForm} variant="outline" className="border-[#333] text-slate-500 text-xs font-bold tracking-wider uppercase h-10 px-4" data-testid="manual-reset">
              <Paintbrush className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
            <Button onClick={() => setShowPopout(true)} variant="outline" className="border-[#333] text-slate-400 text-xs font-bold tracking-wider uppercase h-10 px-4 xl:hidden" data-testid="manual-popout-preview">
              <Maximize2 className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
          </div>
        </div>

        {/* ── Right: Live Preview ── */}
        <div className="hidden xl:block">
          <div className="sticky top-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-red-500" />
                <span className="text-xs font-bold tracking-widest text-white uppercase">Live Preview</span>
              </div>
              <Button onClick={() => setShowPopout(true)} variant="ghost" size="sm" className="text-slate-500 hover:text-white h-7 px-2" data-testid="manual-popout-btn">
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div
              className="bg-white rounded-lg p-5 max-h-[calc(100vh-200px)] overflow-y-auto"
              style={{ color: '#1a1a1a' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHTML) }}
              data-testid="manual-live-preview"
            />
          </div>
        </div>
      </div>

      {/* Popout Preview Dialog */}
      <Dialog open={showPopout} onOpenChange={setShowPopout}>
        <DialogContent className="bg-white border-[#ddd] text-[#1a1a1a] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1a1a1a]">Manual Template Preview</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">This is how the email will look to the recipient.</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <div
              className="prose prose-sm max-w-none"
              style={{ color: '#1a1a1a' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHTML) }}
              data-testid="manual-popout-preview-body"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={downloadPDF} disabled={downloadingPdf} variant="outline" className="text-xs font-bold tracking-wider uppercase" data-testid="manual-popout-pdf">
              {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} PDF
            </Button>
            <Button onClick={() => { setShowPopout(false); setShowEmailDialog(true); }} variant="outline" className="text-xs font-bold tracking-wider uppercase" data-testid="manual-popout-email">
              <Send className="w-3.5 h-3.5 mr-1.5" /> Email
            </Button>
            <Button onClick={copyHTML} variant="outline" className="text-xs font-bold tracking-wider uppercase" data-testid="manual-popout-copy">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy HTML
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wider flex items-center gap-2">
              <Send className="w-5 h-5 text-red-500" /> Send Template via Email
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Sends from samples@etarsp.com</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Recipient Email</Label>
              <Input
                value={emailForm.recipient}
                onChange={e => setEmailForm(p => ({ ...p, recipient: e.target.value }))}
                placeholder="recipient@example.com"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm"
                data-testid="manual-email-recipient"
              />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Subject Line</Label>
              <Input
                value={emailForm.subject}
                onChange={e => setEmailForm(p => ({ ...p, subject: e.target.value }))}
                placeholder="Sample Template Preview"
                className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm"
                data-testid="manual-email-subject"
              />
            </div>
            <Button
              onClick={sendEmail}
              disabled={sendingEmail || !emailForm.recipient.trim()}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11"
              data-testid="manual-email-send"
            >
              {sendingEmail ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
